import React from "react";
import round from "lodash/round";
import { withTranslation } from "react-i18next";
import NavigationComponent from "../components/NavigationComponent";
import moment from "moment";
import RangeContainerComponent from "./RangeContainerComponent";
import TableDailyStatsComponent from "./TableDailyStatsComponent";
import GraphComponent from "../readiness/GraphComponent";
import convert from "convert-units";
import { AthleteLinks } from "../../../../parts/athlete-links";
import { AthleteSelect } from "../../../../parts/inputs/athlete-select-new";
import getAvatarUrl from "../../../../utils/get-avatar-url";
import { RillContext } from "../../../../rill/RillReact/share/base";

const dateFormat = {
  normal: "YYYY-MM-DD",
  calendar: "MMM D, YYYY",
  utc: "YYYYMMDD",
};

const BODY_MEASURES = [
  "Neck",
  "Chest",
  "Left Arm",
  "Right Arm",
  "Waist",
  "Hips",
  "Left Thigh",
  "Right Thigh",
  "Left Calf",
  "Right Calf",
  "Glutes",
];

const BODY_PART_KEYS = {
  Neck: "neck",
  Chest: "chest",
  "Left Arm": "leftArm",
  "Right Arm": "rightArm",
  Waist: "waist",
  Hips: "hips",
  "Left Thigh": "leftThigh",
  "Right Thigh": "rightThigh",
  "Left Calf": "leftCalf",
  "Right Calf": "rightCalf",
  Glutes: "glutes",
};

class BodyMeasurements extends React.Component {
  constructor(props, context) {
    super(props, context);
    this.state = {
      currentAthlete: null,
      dateFrom: null,
      dateTo: null,
      quickDisabled: false,
      rangeSelector: {
        type: "months",
        value: 6,
      }, // in Weeks format
      tooltip: {
        show: false,
        y: 0,
        x: 0,
        content: "",
      },
      measureSelected: BODY_MEASURES[0],
      unitSelected: context.locals.units === "metric" ? "cm" : "inch",
      stats: [],
      lastMeasures: {},
      avg: false,
      comesFromBodyComp: props.comesFromBodyComp,
    };
    this.getArrayFormatted = this.getArrayFormatted.bind(this);
    this.getArrayFiltered = this.getArrayFiltered.bind(this);
    this.updateState = this.updateState.bind(this);
    this.getDateFirstEntry = this.getDateFirstEntry.bind(this);
  }

  getMeasureString(measure = "") {
    if (
      measure.includes("Arm") ||
      measure.includes("Thigh") ||
      measure.includes("Calf")
    ) {
      const splitted = measure.split(" ");
      return `${splitted[0].toLowerCase()}${splitted[1]}`;
    }
    return measure.toLowerCase();
  }

  convertFromCm(value) {
    return parseFloat(round(convert(value).from("cm").to("in"), 2).toFixed(1));
  }

  getArrayFiltered(arr) {
    const { dateFrom, dateTo, rangeSelector } = this.state;
    const { value, type } = rangeSelector;

    if (dateFrom && dateTo) {
      const formatedFrom = parseInt(
        moment(dateFrom, dateFormat.calendar).format(dateFormat.utc)
      );
      const formatedTo = parseInt(
        moment(dateTo, dateFormat.calendar).format(dateFormat.utc)
      );

      const filtered = arr.filter(({ utcDate }) => {
        const statDate = parseInt(utcDate);
        if (statDate === formatedFrom && statDate === formatedTo) return true;
        return !Boolean(statDate < formatedFrom || statDate > formatedTo);
      });
      return filtered;
    }

    if (type === "lifetime") return arr;
    const result = arr.filter(
      ({ date }) =>
        !Boolean(moment(new Date()).subtract(value, type).isAfter(date))
    );
    return result;
  }

  getArrayFormatted(arr) {
    const { measureSelected } = this.state;
    const measureString = this.getMeasureString(measureSelected);
    const result = this.getArrayFiltered(
      arr.sort((a, b) => new Date(a.date) - new Date(b.date))
    )
      .filter(
        (stats) =>
          stats.bodyMeasurements && stats.bodyMeasurements[measureString]
      )
      .map((stats) => {
        const measurement = stats.bodyMeasurements[measureString];
        return {
          value:
            this.state.unitSelected === "cm"
              ? parseFloat(round(measurement, 2).toFixed(1))
              : this.convertFromCm(measurement),
          meta: moment(stats.utcDate, dateFormat.utc).format(
            dateFormat.calendar
          ),
        };
      });

    return result;
  }

  getAvg(stats) {
    if (!stats.length) return false;
    const avg = (
      stats.reduce((prev, next) => {
        return prev + next.value;
      }, 0) / stats.length
    ).toFixed(1);
    return `${avg} ${this.state.unitSelected}`;
  }

  componentDidMount() {
    const { value, type } = this.state.rangeSelector;
    const { dailyStats } = this.context.locals;
    const arrayFormated = this.getArrayFormatted(dailyStats);
    this.setState({
      dateFrom: moment(new Date(), dateFormat.normal)
        .subtract(value, type)
        .format(dateFormat.calendar),
      stats: arrayFormated,
      lastMeasures: this.getLastMeasures(dailyStats),
      avg: this.getAvg(arrayFormated),
    });
  }

  updateState(state) {
    this.setState(state);
  }

  getDateFirstEntry() {
    const {
      locals: { dailyStats },
    } = this.context;
    if (!dailyStats.length) return [];
    const measureString = this.getMeasureString(this.state.measureSelected);
    const result = dailyStats
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .find(
        (stat) => stat.bodyMeasurements && stat.bodyMeasurements[measureString]
      )?.utcDate;
    return result;
  }

  getLastMeasures(stats) {
    const result = BODY_MEASURES.reduce((prev, next) => {
      const measureString = this.getMeasureString(next);
      const result = stats
        .filter(
          (stat) =>
            stat.bodyMeasurements && stat.bodyMeasurements[measureString]
        )
        .reverse()[0];
      const measure = result ? result.bodyMeasurements[measureString] : null;
      return Object.assign(prev, {
        [next]: measure
          ? this.state.unitSelected === "cm"
            ? measure
            : this.convertFromCm(measure)
          : null,
      });
    }, {});
    return result;
  }

  componentDidUpdate(prevProps, prevState) {
    const { locals } = this.context;
    const { dailyStats } = locals;

    const athleteId = locals.athlete
      ? locals.athlete._id
      : locals.activeUser._id;

    //if the athlete is changed
    if (athleteId !== this.state.currentAthlete) {
      //if the range date is not lifetime, we only update the athlete
      if (this.state.rangeSelector.type !== "lifetime") {
        this.setState({
          currentAthlete: athleteId,
        });
        //if range date is lifetime we set a default range
      } else {
        this.setState({
          currentAthlete: athleteId,
          dateFrom: moment("2015-12-31T07:57:45.121Z").format(
            dateFormat.calendar
          ),
        });
      }
    }

    if (
      prevState.dateFrom !== this.state.dateFrom ||
      prevState.dateTo !== this.state.dateTo ||
      prevState.measureSelected !== this.state.measureSelected ||
      prevState.unitSelected !== this.state.unitSelected
    ) {
      const arrayFormated = this.getArrayFormatted(dailyStats);
      this.setState({
        stats: arrayFormated,
        lastMeasures: this.getLastMeasures(dailyStats),
        avg: this.getAvg(arrayFormated),
      });
    }

    //change the stats state to update the render
    if (
      prevState.currentAthlete !== null &&
      this.state.currentAthlete !== athleteId
    ) {
      const arrayFormated = this.getArrayFormatted(dailyStats);
      this.setState({
        stats: arrayFormated,
        lastMeasures: this.getLastMeasures(dailyStats),
        avg: this.getAvg(arrayFormated),
      });
    }
  }

  renderBodyParts() {
    const { t } = this.props;
    const Item = ({
      active = false,
      title = "Neck",
      displayTitle = "Neck",
      lastMeasure = null,
      unit = "cm",
    }) => (
      <tr
        onClick={() => {
          this.setState({ measureSelected: title });
        }}
      >
        <td className={active ? "active-bg" : ""}>
          {active ? <span>{displayTitle}</span> : displayTitle}
        </td>
        <td className={active ? "active-bg" : ""}>
          <span className="tag">
            {lastMeasure ? `${lastMeasure} ${unit}` : <i>-</i>}
          </span>
        </td>
      </tr>
    );
    return (
      <div>
        <p className="s3">{t("bodyMeasurements.selectBodyPart")}</p>
        <section role="table-wrapper">
          <table role="report-table">
            <thead role="table-header">
              <tr>
                <th>{t("bodyMeasurements.bodyPart")}</th>
                <th>{t("bodyMeasurements.lastMeasurement")}</th>
              </tr>
            </thead>
            <tbody role="table-body">
              {BODY_MEASURES.map((measure, idx) => (
                <Item
                  key={idx}
                  title={measure}
                  displayTitle={t(
                    `bodyMeasurements.bodyParts.${BODY_PART_KEYS[measure]}`
                  )}
                  lastMeasure={
                    this.state.lastMeasures && this.state.lastMeasures[measure]
                      ? parseFloat(
                          round(this.state.lastMeasures[measure], 2).toFixed(1)
                        )
                      : null
                  }
                  active={this.state.measureSelected === measure}
                  unit={this.state.unitSelected}
                />
              ))}
            </tbody>
          </table>
        </section>
      </div>
    );
  }

  renderChangeBar() {
    const Item = ({
      center = false,
      number = 0,
      unit = "cm",
      sub = "Start",
    }) => (
      <div data-middle={center ? true : undefined}>
        <span>
          <b>
            {parseFloat(round(number, 2).toFixed(1))} {unit}
          </b>
        </span>

        <h4>{sub}</h4>
      </div>
    );
    const { t } = this.props;
    const { unitSelected, stats } = this.state;
    const start = stats[0].value;
    const end = [...stats].splice(-1)[0].value;
    return (
      <div className="bar-container">
        <Item
          unit={unitSelected}
          number={start}
          sub={t("bodyMeasurements.start")}
        />
        <Item
          unit={unitSelected}
          number={end}
          sub={t("bodyMeasurements.end")}
          center
        />
        <Item
          unit={unitSelected}
          number={parseFloat((end - start).toFixed(1))}
          sub={t("bodyMeasurements.change")}
        />
      </div>
    );
  }

  render() {
    const { locals } = this.context;
    const { t } = this.props;
    const { dateTo, dateFrom, stats, unitSelected, measureSelected, avg } =
      this.state;
    const { query, pathname, athlete } = locals;
    const unitOptions = [
      { name: "Cm", unit: "cm" },
      { name: "Inch", unit: "inch" },
    ];

    return (
      <main role="reports-wrapper">
        <div className="athlete-section-header only-show-mobile">
          <AthleteLinks
            athlete={athlete}
            pathName={pathname}
            classNames={"only-show-mobile"}
            query={query}
          />
          <div className="athlete-select dropdown-athlete">
            <div
              className="avatar"
              style={{
                backgroundImage: `url(${getAvatarUrl(locals?.athlete?._id)})`,
              }}
            />
            <AthleteSelect locals={locals} />
          </div>
        </div>
        <NavigationComponent
          query={query}
          textKey="nav.calendarDayView"
          pathName={pathname}
          classNames="mobile-hidden"
        />
        <article id="body-measurements" role="report-content">
          <RangeContainerComponent
            {...this.state}
            updateState={this.updateState}
            getDateFirstEntry={this.getDateFirstEntry}
            unitOptions={unitOptions}
            reportSelected={t("metrics.bodyMeasurements")}
            locals={locals}
          />
          <GraphComponent
            bodyMeasurement={measureSelected}
            dateTo={dateTo}
            dateFrom={dateFrom}
            series={[stats]}
            colors={["#5BC500"]}
            chartName={unitSelected.toUpperCase()}
            displayLeyend={false}
            displayAvgText={avg}
            chartOptions={{
              showLine: true,
              axisX: {
                showGrid: false,
                scaleMinSpace: 20,
              },
            }}
            autoScale={stats.length}
            areaOpacity={0.2}
            tooltipUnit={unitSelected}
          />
          {stats.length > 1 && this.renderChangeBar()}
          <div className="measurements-tables">
            {this.renderBodyParts()}
            <div>
              <p className="s3">
                {t("bodyMeasurements.measurementLabel", {
                  measure: t(
                    `bodyMeasurements.bodyParts.${BODY_PART_KEYS[measureSelected]}`
                  ),
                })}
              </p>
              <TableDailyStatsComponent
                unitSelected={unitSelected}
                stats={stats}
              />
            </div>
          </div>
        </article>
      </main>
    );
  }
}

BodyMeasurements.contextType = RillContext;

export default withTranslation("athlete-portal")(BodyMeasurements);
